import React, { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import { useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ScrollToTop from "./Footer/ScrollToTop";
import styled, { keyframes } from "styled-components";
import { CartProvider } from "./components/store/CartProvider";

const ImageMap = lazy(() => import("./features/home/ImageMap"));
const Contacto = lazy(() => import("./features/contact/Contacto"));
const Media = lazy(() => import("./features/media/Media"));
const Shop = lazy(() => import("./features/shop/Shop"));
const CartPage = lazy(() => import("./features/shop/CartPage"));
const CheckoutPage = lazy(() => import("./features/shop/CheckoutPage"));
const ProductPage = lazy(() => import("./features/shop/ProductPage"));
const CheckoutSuccess = lazy(() => import("./features/shop/CheckoutSuccess"));
const CheckoutCancelled = lazy(() => import("./features/shop/CheckoutCancelled"));
const AccountPage = lazy(() => import("./features/account/AccountPage"));
const AdminStorePage = lazy(() => import("./features/admin/AdminStorePage"));
const AdminStatsPage = lazy(() => import("./features/admin/AdminStatsPage"));
const Nosotros = lazy(() => import("./features/about/Nosotros"));
const Tickets = lazy(() => import("./features/tickets/Tickets"));
const NotFound = lazy(() => import("./features/notfound/NotFound"));
const AvisoLegal = lazy(() => import("./features/legal/AvisoLegal"));
const PoliticaPrivacidad = lazy(() => import("./features/legal/PoliticaPrivacidad"));
const PoliticaCookies = lazy(() => import("./features/legal/PoliticaCookies"));
const TerminosCondiciones = lazy(() => import("./features/legal/TerminosCondiciones"));
const PoliticaDevolucionesEnvio = lazy(() => import("./features/legal/PoliticaDevolucionesEnvio"));

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const LoadingFallback = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  gap: 20px;
  font-family: "Font";
  color: #FFFFFF;
  font-size: 1rem;
  background-image: url('/images/fondo.jpeg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 107, 53, 0.15);
  border-top: 3px solid #FF6B35;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.span`
  animation: ${pulse} 1.5s ease-in-out infinite;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
`;

function MetaPixelPageView() {
  const location = useLocation();

  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <CartProvider>
            <ScrollToTop />
            <MetaPixelPageView />
            <Suspense fallback={
              <LoadingFallback>
                <Spinner />
                <LoadingText>Cargando...</LoadingText>
              </LoadingFallback>
            }>
              <Routes>
                <Route path="/" element={<ImageMap />} />
                <Route path="/contacto" element={<Contacto />} />
                <Route path="/media" element={<Media />} />
                <Route path="/tienda" element={<Shop />} />
                <Route path="/tienda/carrito" element={<CartPage />} />
                <Route path="/tienda/checkout" element={<CheckoutPage />} />
                <Route path="/tienda/producto/:slug" element={<ProductPage />} />
                <Route path="/tienda/success" element={<CheckoutSuccess />} />
                <Route path="/tienda/cancelled" element={<CheckoutCancelled />} />
                <Route path="/cuenta" element={<AccountPage />} />
                <Route path="/admin/tienda" element={<AdminStorePage />} />
                <Route path="/admin/estadisticas" element={<AdminStatsPage />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/nosotros" element={<Nosotros />} />
                <Route path="/aviso-legal" element={<AvisoLegal />} />
                <Route path="/politica-de-privacidad" element={<PoliticaPrivacidad />} />
                <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
                <Route path="/terminos-y-condiciones" element={<TerminosCondiciones />} />
                <Route path="/devoluciones" element={<PoliticaDevolucionesEnvio />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </CartProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
